import { Edit } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PopoverAG from "components/ui/popoverAG";
import useFormatter from "hooks/useFormatter";
import useQueries from "hooks/useQueries";
import useWindowSize from "hooks/useWindowSize";
import { setLoading } from "lib/Reducer/loadingSlice";
import moment from "moment";
import { MdDelete, MdEdit } from "react-icons/md";
import { TbListDetails } from "react-icons/tb";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { updateCustomerStatus, updateFollowupDate } from "../../api/customer";
import { customer_status } from "utils/constants";

const MainTableRow = ({
  row,
  columns,
  handleDelete,
  setReload,
  openNote,
  setOpenNote,
  handleTableCheckBox,
  selectedRows,
  allCustomers,
  setAllCustomers,
  permissions,
  ...restProps
}) => {
  const { setQueries } = useQueries();
  const { isMini } = useWindowSize();
  const { formatDate } = useFormatter();
  const handleChangeStatus = async (value) => {
    await updateCustomerStatus({
      body_data: { id: row?.id, status: value },
    });
    setAllCustomers(
      allCustomers.map((customer) =>
        customer.id === row.id ? { ...row, status: value } : customer
      )
    );
  };

  const dispatch = useDispatch();

  const handleFollowupDate = async (followup_date) => {
    dispatch(setLoading(true));
    const body_data = {
      lead_id: row?.lead_id,
      followup_date,
    };
    try {
      const result = await updateFollowupDate({ body_data });
      if (result.success) {
        toast.success(result.message);
        setReload((pre) => !pre);
      } else {
        toast.error(result.message);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <TableRow
      {...restProps}
      sx={{
        "& .MuiTableCell-sizeMedium": {
          padding: "10px 0px 10px 16px",
        },
        "& .MuiTableCell-sizeMedium:first-of-type": {
          paddingLeft: "8px",
          paddingRight: "0px",
        },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedRows?.includes(row.id)}
          onChange={(e) => handleTableCheckBox(row.id, e.target.checked)}
          disabled={!permissions["bulk action"] || isMini}
        />
      </TableCell>
      {columns.map((column) => {
        const value = row[column.id];
        return (
          <TableCell key={column.id} align={column.align} style={column.style}>
            {column.id === "status" ? (
              <TextField
                size="small"
                value={row?.status}
                select
                sx={{
                  width: "90px",
                  color: customer_status?.find(
                    (singleStatus) => singleStatus?.id === row?.status
                  )?.color,
                  textAlign: "left",
                  "& > div:before": { content: "none" },
                  "& div": {
                    color: customer_status?.find(
                      (singleStatus) => singleStatus?.id === row?.status
                    )?.color,
                  },
                }}
                variant="standard"
                onChange={(e) => handleChangeStatus(e.target.value)}
              >
                {customer_status?.length > 0 &&
                  customer_status?.map((status) => (
                    <MenuItem value={status?.id} key={status?.id}>
                      {status?.label}
                    </MenuItem>
                  ))}
              </TextField>
            ) : column.id === "last_note" ? (
              <Box
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  "&:hover": {
                    "& svg": {
                      color: (theme) => theme.palette.primary.main,
                    },
                  },
                  "& svg": {
                    position: "absolute",
                    color:
                      openNote === row?.id
                        ? (theme) => theme.palette.primary.main
                        : "#777",
                    top: "0px",
                    right: "0px",
                    cursor: "pointer",
                    fontSize: "17px",
                  },
                }}
                onClick={() => setOpenNote(openNote === row?.id ? "" : row?.id)}
              >
                <Typography
                  sx={{
                    overflow: "hidden",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                    maxHeight: "22px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {value || "-"}
                </Typography>
                <MdEdit />
              </Box>
            ) : column.id === "followup_date" ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>{formatDate(value)}</span>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Custom input"
                    value={value}
                    onChange={(newValue) => {
                      handleFollowupDate(moment(newValue).format("YYYY-MM-DD"));
                    }}
                    renderInput={({ inputRef, inputProps, InputProps }) => (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {InputProps?.endAdornment}
                        <input
                          style={{
                            width: "0px",
                            height: "0px",
                            opacity: 0,
                          }}
                          ref={inputRef}
                          {...inputProps}
                        />
                      </Box>
                    )}
                  />
                </LocalizationProvider>
              </Box>
            ) : column.id === "assignees" ? (
              <PopoverAG items={value} />
            ) : column.id === "action" ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "5px",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Details">
                  <IconButton onClick={() => setQueries(`id=${row?.id}&m=1`)}>
                    <TbListDetails />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    disabled={!permissions.update}
                    onClick={() => setQueries(`id=${row?.id}&m=0`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => handleDelete(row.id)}
                    disabled={!permissions.trash}
                  >
                    <MdDelete />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              value
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default MainTableRow;
